import React from 'react'
import {
  Alert,
  Button,
  Divider,
  Expander,
  ExpanderContent,
  ExpanderHeader,
  Paragraph,
  QuickGlanceActions,
  QuickGlanceOverlay,
  Row,
  Separator,
  colors,
} from '@fs/zion-ui'
import { SocialStar, NoticeLoading, HelpAi } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@fs/zion-flags'
import StreamingFormattedContent from './StreamingFormattedContent'

const SummaryListBlock = ({ title, items }) => {
  if (!items?.length) return null

  return (
    <>
      <Separator size="nano" />
      <Paragraph>{title}</Paragraph>
      <Separator size="nano" />
      <ul>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </>
  )
}

const FullTextSummarization = ({
  stateCurrentImageArk,
  overlay,
  currentImageArk,
  isExpanderOpen,
  handleExpanderToggle,
  handleSummarizeClick,
  summarization,
  summarizationData,
  summaryError,
  fetchSummarization,
  loading,
  streamingContent,
  isStreaming,
}) => {
  const [t] = useTranslation()
  const { rip_sharedImageViewer_fullTextAttachFlow_v2: attachFlowV2 } = useFeatureFlags([
    'rip_sharedImageViewer_fullTextAttachFlow_v2',
  ])
  return (
    <>
      <Expander
        open={isExpanderOpen && currentImageArk === stateCurrentImageArk}
        setOpen={handleExpanderToggle}
        linkName="full-text-summarize-expander"
      >
        <ExpanderHeader>
          <Separator size="nano" />
          {!attachFlowV2.isOn && (
            <Row alignItemsY="middle" guttersY="lg">
              <SocialStar color={colors.blue60} onClick={handleSummarizeClick} data-testid="star-icon" />
              <strong style={{ color: colors.blue60 }}>
                {t('full-text.ai-summarization-header', 'Summarize the document')}
              </strong>
            </Row>
          )}
          {attachFlowV2.isOn && (
            <Row alignItemsY="middle" guttersY="lg">
              <HelpAi onClick={handleSummarizeClick} data-testid="ai-icon" />
              <strong>{t('full-text.ai-summarization-header', 'Summarize the document')}</strong>
            </Row>
          )}
          <Separator size="nano" />
        </ExpanderHeader>
        <QuickGlanceOverlay {...overlay} title={t('full-text.prompt-title', 'Prompt sent to ChatGPT')}>
          <Paragraph size="sm">{summarization}</Paragraph>
          <QuickGlanceActions>
            <Button
              onClick={overlay.close}
              keyline="end"
              data-testid="close-quick-glance-button"
              linkName="close-quick-glance"
            >
              {t('full-text.close-button', 'Close')}
            </Button>
          </QuickGlanceActions>
        </QuickGlanceOverlay>
        <ExpanderContent>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <NoticeLoading />
            </div>
          )}
          {!loading && isStreaming && (
            <StreamingFormattedContent streamingContent={streamingContent} isStreaming={isStreaming} />
          )}
          {!loading && !isStreaming && summarizationData && (
            <>
              <span style={{ color: colors.gray70 }}>
                <p>{t('full-text.ai-generated', 'AI-generated from the transcription below.')}</p>
              </span>
              <Separator size="nano" />
              <Paragraph size="sm">{summarizationData.summary}</Paragraph>
              <SummaryListBlock title={t('full-text.dates', 'Dates')} items={summarizationData.dates} />
              <SummaryListBlock
                title={t('full-text.names-and-relationships', 'Names and Relationships')}
                items={summarizationData.namesAndRelationships}
              />
              <Separator size="nano" />
            </>
          )}
          {!loading && !isStreaming && summaryError && (
            <>
              <Alert type="warning">
                {t('full-text.summary-error', 'We encountered an error creating the summary. Please try again later.')}
              </Alert>
              <Separator size="xs" />
              <Button emphasis="medium" onClick={fetchSummarization}>
                {t('full-text.summary-error-retry', 'Try Again')}
              </Button>
            </>
          )}
          <Separator size="xs" />
        </ExpanderContent>
      </Expander>
      <Divider />
    </>
  )
}

export default FullTextSummarization
